import { Trans, useLingui } from "@lingui/macro";
import { RefreshCw, X } from "lucide-react";

import { cn } from "@app/lib/utils";
import type { DispersionRow, SingularityResponse, SingularityRow } from "@app/types/dashboard-pathway";

/** `(dimension, value) → label`: a row's value is a term, drawn in the reader's language. */
type LabelOf = (dimension: string, value: string) => string;

/**
 * Card 2 — what this practice does differently from its peers, or where it sits among them.
 *
 * Every row is read the same way, as a sentence first and a picture second: the field, the value,
 * both shares on one graduated axis. Rows are grouped under their field, so « Hernie discale » under
 * the indication and under the diagnosis read as two facts of two fields rather than a repeat.
 */

const pctText = (value: number) => `${(Math.round(value * 1000) / 10).toLocaleString("fr-FR")}\u202f%`;

const TICKS = [0, 0.25, 0.5, 0.75, 1];

const at = (value: number) => `${Math.max(0, Math.min(100, value * 100))}%`;

/** Rows under the field they describe, fields in the order their first row was ranked. */
function byField<T extends { dimension: string; dimension_label: string }>(rows: T[]): [string, T[]][] {
  const groups = new Map<string, { label: string; rows: T[] }>();
  for (const row of rows) {
    const group = groups.get(row.dimension) ?? { label: row.dimension_label, rows: [] };
    group.rows.push(row);
    groups.set(row.dimension, group);
  }
  return [...groups.values()].map((group) => [group.label, group.rows]);
}

/** The shared 0–100 % axis: faint gridlines inside a track, labels once under the card. */
function Grid() {
  return (
    <>
      {TICKS.slice(1, -1).map((tick) => (
        <span key={tick} className="absolute inset-y-0 w-px bg-slate-200" style={{ left: at(tick) }} />
      ))}
    </>
  );
}

function AxisLabels() {
  return (
    <div className="relative mt-1 h-3 text-3xs text-slate-400 tabular-nums">
      {TICKS.map((tick) => (
        <span
          key={tick}
          className={cn("absolute", tick === 0 ? "" : tick === 1 ? "-translate-x-full" : "-translate-x-1/2")}
          style={{ left: at(tick) }}
        >
          {pctText(tick)}
        </span>
      ))}
    </div>
  );
}

function Unit({ family, count }: { family: "acte" | "patient"; count: number }) {
  return family === "acte" ? (
    <Trans>{count} épisodes</Trans>
  ) : (
    <Trans>{count} patients</Trans>
  );
}

function GapRows({ rows, labelOf }: { rows: SingularityRow[]; labelOf: LabelOf }) {
  return (
    <div className="space-y-4">
      {byField(rows).map(([field, fieldRows]) => (
        <section key={field}>
          <h4 className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-slate-500">{field}</h4>
          <div className="space-y-3">
            {fieldRows.map((row) => {
              const above = row.delta_pp >= 0;
              const points = Math.abs(Math.round(row.delta_pp)).toLocaleString("fr-FR");
              const low = Math.min(row.share, row.reference_share);
              const high = Math.max(row.share, row.reference_share);
              return (
                <div key={`${row.dimension}:${row.value}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="min-w-0 truncate text-xs font-semibold text-slate-900">
                      {labelOf(row.dimension, row.value)}
                    </p>
                    {/* The gap is the one number set apart. "points" written out: "pt" alone gets
                        read as a percentage of a percentage. */}
                    <span
                      className={cn(
                        "shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums",
                        above ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-700",
                      )}
                    >
                      {above ? "+" : "−"}
                      {points} <Trans>points</Trans>
                    </span>
                  </div>

                  {/* Both levels on one axis, the gap drawn between them: 67 % against 2 % is read,
                      where a bar from a centre line only said "+65" and hid what it was a gap between. */}
                  <div className="relative mt-1.5 h-3">
                    <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-slate-200" />
                    <Grid />
                    <span
                      className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-primary/30"
                      style={{ left: at(low), width: `calc(${at(high)} - ${at(low)})` }}
                    />
                    <span
                      className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-slate-500 bg-white"
                      style={{ left: at(row.reference_share) }}
                    />
                    <span
                      className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-2 ring-white"
                      style={{ left: at(row.share) }}
                    />
                  </div>

                  {/* Numerator AND denominator on both sides: 14 of 32 is a pattern, 3 of 7 is not. */}
                  <p className="mt-1 text-2xs text-slate-500 tabular-nums">
                    <span className="font-medium text-slate-800">
                      <Trans>Vous : {pctText(row.share)}</Trans>
                    </span>{" "}
                    (<Trans>
                      {row.episodes} sur <Unit family={row.family} count={row.documented} />
                    </Trans>
                    ){" · "}
                    <Trans>vos confrères : {pctText(row.reference_share)}</Trans> (
                    <Trans>
                      {row.reference_episodes} sur <Unit family={row.family} count={row.reference_documented} />
                    </Trans>
                    )
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      ))}
      <AxisLabels />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-2 text-2xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-primary" />
          <Trans>vous</Trans>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border-2 border-slate-500 bg-white" />
          <Trans>vos confrères (vous exclu)</Trans>
        </span>
      </div>
    </div>
  );
}

/** Where the reader falls against the central half of practitioners, in words. */
function Position({ row }: { row: DispersionRow }) {
  if (row.me == null) return null;
  const me = <span className="font-semibold text-primary">{pctText(row.me)}</span>;
  if (row.me > row.p75) {
    return (
      <Trans>
        Vous : {me} — plus que la plupart des praticiens.
      </Trans>
    );
  }
  if (row.me < row.p25) {
    return (
      <Trans>
        Vous : {me} — moins que la plupart des praticiens.
      </Trans>
    );
  }
  return (
    <Trans>
      Vous : {me} — comme la moitié centrale des praticiens.
    </Trans>
  );
}

function DispersionRows({ rows, hasMe, labelOf }: { rows: DispersionRow[]; hasMe: boolean; labelOf: LabelOf }) {
  return (
    <div className="space-y-4">
      {byField(rows).map(([field, fieldRows]) => (
        <section key={field}>
          <h4 className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-slate-500">{field}</h4>
          <div className="space-y-3">
            {fieldRows.map((row) => (
              <div key={`${row.dimension}:${row.value}`}>
                <p className="text-xs font-semibold text-slate-900">{labelOf(row.dimension, row.value)}</p>
                <p className="mt-0.5 text-2xs text-slate-600 tabular-nums">
                  {hasMe && row.me != null && (
                    <>
                      <Position row={row} />{" "}
                    </>
                  )}
                  {/* The SPREAD, never the headcount: on a small speciality a count plus a range is
                      one arithmetic step from naming the outlier. */}
                  <Trans>
                    La moitié des praticiens est entre {pctText(row.p25)} et {pctText(row.p75)} ; tous vont
                    de {pctText(row.min)} à {pctText(row.max)}.
                  </Trans>
                </p>
                <div className="relative mt-1.5 h-4">
                  <Grid />
                  {/* Range of every practitioner, then the central half on it, then the median. */}
                  <span
                    className="absolute top-1/2 h-px -translate-y-1/2 bg-slate-400"
                    style={{ left: at(row.min), width: `calc(${at(row.max)} - ${at(row.min)})` }}
                  />
                  <span className="absolute top-1 h-2 w-px bg-slate-400" style={{ left: at(row.min) }} />
                  <span className="absolute top-1 h-2 w-px bg-slate-400" style={{ left: at(row.max) }} />
                  <span
                    className="absolute top-1/2 h-2.5 -translate-y-1/2 rounded-sm bg-slate-300"
                    style={{ left: at(row.p25), width: `calc(${at(row.p75)} - ${at(row.p25)})` }}
                  />
                  <span
                    className="absolute top-1/2 h-2.5 w-0.5 -translate-y-1/2 bg-slate-700"
                    style={{ left: at(row.median) }}
                  />
                  {hasMe && row.me != null && (
                    <span
                      className="absolute inset-y-0 w-1 -translate-x-1/2 rounded-sm bg-primary ring-1 ring-white"
                      style={{ left: at(row.me) }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
      <AxisLabels />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-2 text-2xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-px w-4 bg-slate-400" />
          <Trans>de l'un à l'autre extrême</Trans>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-4 rounded-sm bg-slate-300" />
          <Trans>moitié centrale des praticiens</Trans>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-0.5 bg-slate-700" />
          <Trans>médiane</Trans>
        </span>
        {hasMe && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-1 rounded-sm bg-primary" />
            <Trans>vous</Trans>
          </span>
        )}
      </div>
    </div>
  );
}

export interface SingularityCardProps {
  /** DOM anchor, so a question chip can bring the reader to the card it just reconfigured. */
  id: string;
  data: SingularityResponse | null;
  loading: boolean;
  error: string | null;
  title: string;
  /** False when the reader is not in the distribution — see `PathwayBody`'s `isGlobalView`. */
  hasMe: boolean;
  family: "acte" | "patient" | null;
  onChangeFamily: (family: "acte" | "patient" | null) => void;
  onRemove?: () => void;
  /** Draws a row's value — a term — in the reader's language. */
  labelOf: LabelOf;
}

export default function SingularityCard({
  id,
  data,
  loading,
  error,
  title,
  hasMe: readerHasMe,
  family,
  onChangeFamily,
  onRemove,
  labelOf,
}: SingularityCardProps) {
  const { t } = useLingui();
  const dispersion = data?.mode === "dispersion";
  /* Whether the reader is IN the distribution, which decides the card's whole framing. The server
     knows for a real account — its own dossiers place it — and the caller knows for a previewed
     global view; a "me" exists only when NEITHER says otherwise. */
  const hasMe = readerHasMe && data?.has_me !== false;

  return (
    <section id={id} className="scroll-mt-24 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <header className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <p className="text-2xs text-slate-500">
            {dispersion ? (
              hasMe ? (
                <Trans>Pour chaque pratique, votre part parmi celles des praticiens de la spécialité, à filtres identiques.</Trans>
              ) : (
                <Trans>Les pratiques dont la part varie le plus d'un praticien à l'autre, à filtres identiques.</Trans>
              )
            ) : (
              <Trans>
                Vos parts comparées à celles de vos confrères, à filtres identiques — seuls les écarts qu'un test
                distingue du hasard.
              </Trans>
            )}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Two families in one ranking by default; each row says which it belongs to by its unit. */}
          <div className="inline-flex items-center rounded-md bg-slate-100 p-0.5">
            {([null, "acte", "patient"] as const).map((value) => (
              <button
                key={value ?? "tous"}
                type="button"
                onClick={() => onChangeFamily(value)}
                aria-pressed={family === value}
                className={cn(
                  "rounded px-2 py-0.5 text-2xs font-medium transition-colors",
                  family === value ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700",
                )}
              >
                {value === null ? t`Tout` : value === "acte" ? t`Gestes` : t`Patients`}
              </button>
            ))}
          </div>
          {onRemove && (
            <button
              type="button"
              aria-label={t`Retirer la carte`}
              onClick={onRemove}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </header>

      {loading && !data && (
        <div className="flex h-32 items-center justify-center gap-2 text-sm text-slate-400">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          <Trans>Chargement...</Trans>
        </div>
      )}

      {error && !data && (
        <p className="py-8 text-center text-sm text-slate-400">
          <Trans>Cette comparaison n'est pas disponible sur ce périmètre.</Trans>
        </p>
      )}

      {/* Named refusals rather than an empty card: a comparison that cannot be made must say why,
          because a row of zeros invites the reader to believe a comparison happened. */}
      {data && !data.available && (
        <p className="py-8 text-center text-sm text-slate-500">
          {data.reason === "single_practitioner" && (
            <Trans>Un seul praticien sur ce périmètre — pas de dispersion à montrer.</Trans>
          )}
          {data.reason === "reference_is_selection" && (
            <Trans>Aucun confrère sur ce périmètre : la comparaison se ferait avec vous-même.</Trans>
          )}
          {data.reason === "no_gap" && (
            <Trans>
              Aucun écart avec vos confrères ne se distingue du hasard sur cette sélection ({data.tested ?? 0}{" "}
              valeurs examinées).
            </Trans>
          )}
          {data.reason === "empty" && <Trans>Aucun épisode sur cette sélection.</Trans>}
        </p>
      )}

      {data?.available &&
        (dispersion ? (
          <DispersionRows rows={data.rows} hasMe={hasMe} labelOf={labelOf} />
        ) : (
          <GapRows rows={data.rows} labelOf={labelOf} />
        ))}

      {data?.available && !dispersion && (
        <p className="mt-2 text-2xs text-slate-400">
          <Trans>
            {data.tested ?? 0} valeurs examinées ; {data.significant ?? data.rows.length} écarts se distinguent du
            hasard (χ² ou test exact de Fisher, corrigés par Benjamini-Hochberg), les plus grands sont montrés.
          </Trans>
        </p>
      )}
      {/* The peers are read in aggregate, so a range reads at the grain the board publishes for them
          (PT-144) — even in « Mes patients », where the counters above answer it to the day. */}
      {data?.available && (data.widened?.length ?? 0) > 0 && (
        <p className="mt-1 text-2xs text-slate-400">
          <Trans>
            Ici, les plages filtrées sont lues à l'année ou à la tranche entière : vos confrères sont lus en
            agrégat.
          </Trans>
        </p>
      )}
    </section>
  );
}
